import React, { useState } from "react";
import { Wand2, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getProfileAutofillHandler } from "../context/ProfileAutofillContext";

interface Props {
  close?: () => void;
  onAutoFill?: (url: string) => void | Promise<void>;
}

const ProfileUrlPopup: React.FC<Props> = ({ close, onAutoFill }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);

const handleAutoFill = async () => {
  if (!url.trim()) return;

  setLoading(true);

  try {
    try {
      try {
        const globalHandler = getProfileAutofillHandler();

        if (globalHandler) {
          await globalHandler(url.trim());
        } else if (onAutoFill) {
          await onAutoFill(url.trim());
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
        close?.();
      }

      if (onAutoFill) {
        close?.();
        onAutoFill(url.trim()); 
        return;
      }

      close?.();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }

    if (onAutoFill) {
      close?.();
      onAutoFill(url.trim()); // ❌ no await
      return;
    }

    close?.();
   
  } catch (err) {
    console.error(err);
  } finally {
    setLoading(false);
  }
};

  return (
    <div className="p-6 max-w-lg bg-white rounded-md">
      <div className="flex justify-between items-start mb-4">
        <h3 className="text-lg font-semibold">{t("add_public_url")}</h3>
      </div>

      <p className="text-sm text-gray-700 mb-4">{t("ai_analyze_profile")}</p>
      <p className="text-sm text-gray-500 mb-2">{t("url_example")}</p>
      <p className="text-sm text-gray-500 mb-4">{t("public_link_info")}</p>

      <label className="block text-sm font-medium text-slate-700 mb-2">
        {t("public_url_label")}
      </label>

      <input
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        placeholder={t("url_placeholder")}
        disabled={loading}
        className="w-full mb-4 px-3 py-2 border rounded-md disabled:opacity-60"
      />

      <div className="flex flex-col items-center gap-3 md:flex-row md:items-stretch">
        <button
          onClick={close}
          disabled={loading}
          className="w-full md:w-1/2 bg-transparent hover:bg-[#d7d7fc] text-base border-purple-600 border text-purple-600 font-semibold py-2.5 rounded-md transition"
        >
          {t("cancel")}
        </button>

        <button
          onClick={handleAutoFill}
          disabled={!url.trim() || loading}
          className="flex w-full md:w-1/2 items-center justify-center hover:bg-[#d7d7fc] hover:text-purple-600 border border-purple-600 gap-2 px-6 py-2 theme-bg-trinary text-white rounded-md font-semibold shadow-md disabled:opacity-50 transition-colors"
        >
          {loading ? (
            <span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
          ) : (
            <Wand2 className="h-5 w-5" />
          )}
          <span>{loading ? t("processing") : t("auto_fill_url")}</span>
        </button>
      </div>
    </div>
  );
};

export default ProfileUrlPopup;
